/**
 * WORKER SETTINGS TAB
 *
 * Local worker management inside the Settings modal.
 * Start/stop/restart controls, job list, log viewer, dependency check.
 */

import { useState } from "react";
import {
  Play,
  Square,
  RotateCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWorkerStore } from "@/stores/workerStore";
import { WorkerLogViewer } from "@/features/factory/worker/WorkerLogViewer";
import { DepsCheckDialog } from "@/features/factory/worker/DepsCheckDialog";
import type { LocalJob, WorkerState } from "@/types/worker";

const STATE_CONFIG: Record<WorkerState, { label: string; color: string }> = {
  stopped: { label: "Stopped", color: "text-muted-foreground" },
  starting: { label: "Starting...", color: "text-yellow-500" },
  running: { label: "Running", color: "text-green-500" },
  error: { label: "Error", color: "text-red-500" },
};

const JOB_STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }> = {
  queued: { variant: "outline", icon: Clock },
  running: { variant: "default", icon: Loader2 },
  completed: { variant: "secondary", icon: CheckCircle2 },
  failed: { variant: "destructive", icon: XCircle },
  cancelled: { variant: "outline", icon: XCircle },
};

function JobRow({ job }: { job: LocalJob }) {
  const config = JOB_STATUS_BADGE[job.status] ?? JOB_STATUS_BADGE.queued;
  const Icon = config.icon;
  const isRunning = job.status === "running";

  return (
    <div className="flex items-center gap-2 py-1.5 text-sm">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isRunning ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-0">
        <div className="truncate font-mono text-xs">{job.taskId}</div>
        <div className="text-xs text-muted-foreground">{job.eventType}</div>
      </div>
      <Badge variant={config.variant} className="text-[10px] h-5">
        {job.status}
      </Badge>
      {job.error && (
        <span className="text-xs text-red-400 truncate max-w-[120px]" title={job.error}>
          {job.error}
        </span>
      )}
    </div>
  );
}

export function WorkerSettingsTab() {
  const workerState = useWorkerStore((s) => s.workerState);
  const jobs = useWorkerStore((s) => s.jobs);
  const depsStatus = useWorkerStore((s) => s.depsStatus);
  const start = useWorkerStore((s) => s.start);
  const stop = useWorkerStore((s) => s.stop);
  const restart = useWorkerStore((s) => s.restart);

  const [showDeps, setShowDeps] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  const stateConfig = STATE_CONFIG[workerState];
  const isRunning = workerState === "running";
  const isStopped = workerState === "stopped";
  const isStarting = workerState === "starting";

  const handleAction = async (fn: () => Promise<void>) => {
    setActionPending(true);
    try {
      await fn();
    } catch (err) {
      console.error("Worker action failed:", err);
    } finally {
      setActionPending(false);
    }
  };

  const activeJobs = jobs.filter((j) => j.status === "running" || j.status === "queued");
  const recentJobs = jobs.slice(-10).reverse();

  return (
    <>
      <div className="space-y-6">
        {/* Header + status */}
        <div>
          <h3 className="text-base font-semibold mb-1">Local Worker</h3>
          <p className="text-sm text-muted-foreground">
            Manage the local Python worker process for running tasks.
          </p>
        </div>

        {/* Status + Controls */}
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${stateConfig.color}`}>
            {stateConfig.label}
          </span>
          <div className="flex-1" />
          {isStopped || workerState === "error" ? (
            <Button
              size="sm"
              onClick={() => handleAction(start)}
              disabled={actionPending || isStarting}
              className="gap-1.5"
            >
              {actionPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Start
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleAction(stop)}
                disabled={actionPending || isStarting}
                className="gap-1.5"
              >
                {actionPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                Stop
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction(restart)}
                disabled={actionPending || isStarting}
                className="gap-1.5"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Restart
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowDeps(true)}
            className="gap-1.5"
          >
            <Settings2 className="w-3.5 h-3.5" />
            Deps
          </Button>
        </div>

        {/* Deps warning */}
        {depsStatus && !depsStatus.all_ok && (
          <button
            onClick={() => setShowDeps(true)}
            className="flex items-center gap-2 text-xs text-yellow-500 hover:underline"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            Some dependencies are missing
          </button>
        )}

        <Separator />

        {/* Jobs */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Jobs {activeJobs.length > 0 && `(${activeJobs.length} active)`}
          </div>
          {recentJobs.length === 0 ? (
            <p className="text-xs text-muted-foreground">No jobs yet</p>
          ) : (
            <ScrollArea className="max-h-[180px]">
              {recentJobs.map((job) => (
                <JobRow key={job.id} job={job} />
              ))}
            </ScrollArea>
          )}
        </div>

        <Separator />

        {/* Logs */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">Logs</div>
          <div className="h-[200px]">
            <WorkerLogViewer />
          </div>
        </div>
      </div>

      <DepsCheckDialog open={showDeps} onOpenChange={setShowDeps} />
    </>
  );
}
